import { useCallback, useRef } from 'react';
import { dateFormat } from 'utils/shared/utils';
import { EcommerceImpression, EcommerceProductDetails, EcommercePurchase } from 'ts/shared/interfaces/Ecommerce';
import { useSearchParams } from 'react-router-dom';
import { getDaysDifference } from 'utils/utils';
import { getBounds } from 'utils/flight/flight';

const useDataLayer = () => {
  const [urlParams] = useSearchParams();
  const ecommerceFlag = useRef(localStorage.getItem('__e_comerce'));
  const initDataLayer = useCallback(() => {
    if (typeof window !== 'undefined' && !window.dataLayer) {
      window.dataLayer = [];
    }
  }, []);

  const pushImpression = useCallback(
    (flights: any[]) => {
      if (!ecommerceFlag.current) {
        return;
      }
      const token = sessionStorage.getItem('searchTokenFlights');
      const areImpressions = sessionStorage.getItem('flightImpressions' + token);
      if (areImpressions) {
        return;
      }
      initDataLayer();
      try {
        if (typeof window !== 'undefined' && window.dataLayer) {
          const departureDate = urlParams.get('departureDate') || '';
          const arrivalDate = urlParams.get('arrivalDate') || '';
          const nights = getDaysDifference(departureDate, arrivalDate);
          const isOneWay = urlParams.get('searchType') === '0';
          const impressions = flights.map((flt, index) => ({
            id: token,
            name: `${urlParams.get('departureCode')} ${urlParams.get(
              'arrivalCode'
            )} ${departureDate} ${nights} Nights`,
            category: 'FLT',
            price: Number(flt?.flighPrice?.grossAndTaxWithMarkup?.value || 0),
            brand: flt?.airlineCode || '',
            variant: isOneWay ? 'הלוך' : 'הלוך חזור',
            list: 'תוצאות חיפוש',
            position: index,
            dimension1: flt?.outbound?.gdsFlyAirs?.length - 1 || 0,
            dimension2: nights,
            dimension3: '',
          }));

          window.dataLayer.push({
            event: 'impression',
            ecommerce: {
              impressions,
            },
          } as EcommerceImpression);

          sessionStorage.setItem('flightImpressions' + token, '1');
        }
      } catch (error) {
        console.error('Error pushing impression to dataLayer:', error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initDataLayer]
  );

  const pushFlightProductDetails = useCallback(
    (flight: any) => {
      if (!ecommerceFlag.current) {
        return;
      }
      initDataLayer();
      try {
        if (typeof window === 'undefined' || !window.dataLayer) {
          return;
        }

        const departureCode = flight.outbound?.departureAirport?.code || '';
        const arrivalCode = flight.outbound?.arrivalAirport?.code || '';
        const bounds = getBounds(flight);
        const nights = getDaysDifference(
          bounds[0].departureDateTime,
          bounds[bounds.length - 1].arrivalDateTime
        );
        const departureDate = dateFormat(flight.outbound.departureDateTime, 'dd-MM-yy');
        const name = `${departureCode} ${arrivalCode} ${departureDate} ${nights} Nights`;

        const flightPrice = flight.flighPrice?.grossAndTaxWithMarkup?.value;
        const price = Number(flightPrice.toFixed(2));

        const brand = (flight.airlineCode || 'FLT').replace(/'/g, '');

        const isOneWay = flight.routeType === 'OneWay';
        const variant = isOneWay ? 'הלוך' : 'הלוך חזור';

        const stops = flight.outbound?.gdsFlyAirs?.length - 1 || 0;

        const data = {
          event: 'productDetails',
          ecommerce: {
            id: urlParams.get('searchToken') || '',
            name: name,
            category: 'FLT',
            price: price,
            brand: brand,
            variant: variant,
            dimension1: stops,
            dimension2: nights,
            dimension3: '',
          },
        } as EcommerceProductDetails;
        window.dataLayer.push(data);
      } catch (error) {
        console.error('Error pushing flight product details to dataLayer:', error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initDataLayer]
  );

  const pushFlightPurchase = useCallback(() => {
    if (!ecommerceFlag.current) {
      return;
    }
    initDataLayer();
    if (typeof window === 'undefined' || !window.dataLayer) {
      return;
    }

    try {
      const orderStr = sessionStorage.getItem('flightSuccessOrder');
      if (!orderStr) {
        console.warn('No flightSuccessOrder found in sessionStorage');
        return;
      }

      const order = JSON.parse(orderStr);
      const flight = order?.flight;

      if (!flight) {
        console.warn('Invalid order structure');
        return;
      }

      const orderId = order.orderId;
      const transactionId = order.transactionId || (orderId ? `RQ${orderId}` : '');
      const trackingKey = `flightPurchaseTracked_${orderId || transactionId}`;

      if (sessionStorage.getItem(trackingKey)) {
        return;
      }

      const departureCode = flight.outbound?.departureAirport?.code || '';
      const arrivalCode = flight.outbound?.arrivalAirport?.code || '';
      const bounds = getBounds(flight);
      const nights = getDaysDifference(
        bounds[0].departureDateTime,
        bounds[bounds.length - 1].arrivalDateTime
      );
      const departureDate = dateFormat(flight.outbound.departureDateTime, 'dd-MM-yy');
      const name = `${departureCode} ${arrivalCode} ${departureDate} ${nights} Nights`;

      const price = order.totalPrice || flight.flighPrice?.grossAndTaxWithMarkup?.value || 0;
      const formattedPrice = Number(price.toFixed(2));

      const brand = (flight.airlineCode || 'FLT').replace(/'/g, '');

      const isOneWay = order.isOneWayFlight || flight.routeType === 'OneWay';
      const variant = isOneWay ? 'הלוך' : 'הלוך חזור';

      const stops = flight.outbound?.gdsFlyAirs?.length - 1 || 0;

      const paymentOrder = sessionStorage.getItem('flightPaymentOrder') || '';
      const paymentOrderData = JSON.parse(paymentOrder);

      window.dataLayer.push({
        event: 'purchase',
        ecommerce: {
          purchase: {
            actionField: {
              id: paymentOrderData?.transactionId || '',
              revenue: price + '',
            },
            products: [
              {
                id: sessionStorage.getItem('searchFlightToken') || '',
                name: name,
                category: 'FLT',
                price: formattedPrice,
                brand: brand,
                variant: variant,
                dimension1: stops,
                dimension2: nights,
                dimension3: '',
                quantity: 1,
                coupon: '',
              },
            ],
          },
        },
      } as EcommercePurchase);

      sessionStorage.setItem(trackingKey, 'true');
    } catch (error) {
      console.error('Error pushing flight purchase to dataLayer:', error);
    }
  }, [initDataLayer]);

  return {
    pushImpression,
    pushFlightProductDetails,
    pushFlightPurchase,
  };
};

export default useDataLayer;
